/* eslint-disable no-unused-vars */
import React, {
    useMemo
} from 'react';
import {
    Label
} from 'shared/components/ui/label';
import {
    Code, AlertCircle
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';

/**
 * InputMappingSection — assistant-level mapping from each wired opcode's
 * declared `inputs[]` (the runtime inputs that opcode expects) to the
 * assistant's typed_input fields (`intent` / `context.<key>` /
 * `propensity.<key>` / `entity.<schema-prop>`).
 *
 * Lives BETWEEN the Typed Input and OpCodes sections so the mental model is
 * "I declare what the assistant carries (typed_input), then I wire each
 * behavior (opcode) to draw from those carriers". Reactive in both
 * directions: editing assistant.typed_input refreshes the cascading-picker
 * options instantly; editing an opcode's inputs[] (via the inline opcode
 * editor) refreshes the row list as soon as the opcode is saved (the
 * shared `useOpcodeMeta` hook re-fetches on opcode_id-set change AND
 * accepts patches from the inline editor's `onSaved` callback).
 *
 * SCHEMAS.md §5: `assistant.opcodes[i].input_mapping` is a
 * `{<opcode_runtime_input_name>: "<context|propensity|intent|entity>.<field>"}`
 * map. The router uses it at intent-routing time to build `runtime_inputs`
 * for the chosen opcode from the assistant's typed_input bag. Values must
 * match `^(context|propensity|intent|entity)\.\w+$`.
 */
export default function InputMappingSection({
    a, onPatch, opcodeMeta
}) {
    const opcodes = Array.isArray(a.opcodes) ? a.opcodes : [];
    // Shared cache from AssistantWorkspace — guarantees this section sees
    // input changes from inline opcode saves the same instant OpCodesSection does.
    const meta = opcodeMeta || {};

    // Cascading-picker options (rebuilt on every typed_input change so the
    // user sees their freshly-added context/propensity/entity keys).
    const options = useMemo(() => buildOptions(a.typed_input), [a.typed_input]);

    function setMapping(opcodeIdx, inputName, target) {
        const next = [...opcodes];
        const cur = {
            ...(next[opcodeIdx] || {})
        };
        const map = {
            ...(cur.input_mapping || {})
        };
        if (!target) delete map[inputName];
        else map[inputName] = target;
        cur.input_mapping = map;
        next[opcodeIdx] = cur;
        onPatch({
            opcodes: next
        });
    }

    if (opcodes.length === 0) {
        return (
            <div className="rounded-md border border-dashed bg-muted/20 p-3 text-[11.5px] text-muted-foreground">
                No opcodes wired to this assistant yet. Add at least one in the
                <b> OpCodes </b> section below — its declared inputs will appear here for mapping.
            </div>
        );
    }

    const ti = a.typed_input || {};
    const noTypedInputs =
        (ti.context_keys || []).length === 0
        && (ti.propensity_keys || []).length === 0
        && Object.keys(ti.entity_schema?.properties || {}).length === 0;

    return (
        <div className="space-y-3">
            <p className="text-[11.5px] text-muted-foreground">
                For each opcode wired to this assistant, pick which typed-input field
                carries each declared runtime input. Unmapped inputs are skipped at runtime.
            </p>

            {noTypedInputs && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                    <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    <div>
                        The assistant has no typed-input fields declared yet. Add
                        <b> context </b> / <b> propensity </b> / <b> entity </b> fields in the
                        <b> Typed Input </b> section above — they'll appear as picker options here.
                        <b> Intent </b> is always available.
                    </div>
                </div>
            )}

            {opcodes.map((op, opcIdx) => {
                const m = meta[op.opcode_id] || {};
                const inputs = Array.isArray(m.inputs) ? m.inputs : [];
                const title = m.title || op.opcode_id || '(unset opcode)';
                const value = op.input_mapping || {};
                return (
                    <div key={op.opcode_id || opcIdx} className="rounded-md border bg-card">
                        <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
                            <Code className="h-3.5 w-3.5 text-violet-500" />
                            <span className="font-semibold text-[13px] truncate" title={title}>{title}</span>
                            <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                                {inputs.length === 0
                                    ? 'no inputs'
                                    : `${inputs.length} input${inputs.length === 1 ? '' : 's'}`}
                            </span>
                            {m.missing && (
                                <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-800">
                                    missing
                                </span>
                            )}
                        </div>
                        <div className="p-2">
                            {inputs.length === 0 ? (
                                <div className="text-[11.5px] text-muted-foreground italic px-1 py-0.5">
                                    This opcode declares no runtime inputs. Add some inside the opcode
                                    editor (in the OpCodes section below) to enable mapping here.
                                </div>
                            ) : (
                                <div className="space-y-1.5">
                                    {inputs.map((inp) => {
                                        const cur = value[inp.name] || '';
                                        const valid = !cur || /^(context|propensity|intent|entity)\.\w+$/.test(cur);
                                        return (
                                            <div key={inp.name} className="grid grid-cols-[200px_1fr] items-center gap-2">
                                                <div className="min-w-0">
                                                    <div className="font-mono text-[12px] truncate" title={inp.name}>{inp.name}</div>
                                                    <div className="text-[10px] text-muted-foreground">
                                                        <span className="rounded bg-muted px-1 font-semibold uppercase mr-1">{inp.type || 'any'}</span>
                                                        {inp.required ? 'required' : 'optional'}
                                                        {inp.description ? ` — ${inp.description}` : ''}
                                                    </div>
                                                </div>
                                                <select
                                                    value={cur}
                                                    onChange={(e) => setMapping(opcIdx, inp.name, e.target.value)}
                                                    className={cn(
                                                        'block w-full rounded-md border bg-background px-2 py-1 text-[12px] font-mono',
                                                        !valid && 'border-destructive'
                                                    )}
                                                >
                                                    {options.map((opt) => (
                                                        <option key={opt.value || '__empty'} value={opt.value}>
                                                            {opt.group ? `${opt.group} → ${opt.label}` : opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function buildOptions(typedInput) {
    const ti = typedInput || {};
    const contextKeys = Array.isArray(ti.context_keys) ? ti.context_keys : [];
    const propensityKeys = Array.isArray(ti.propensity_keys) ? ti.propensity_keys : [];
    const entityProps = (ti.entity_schema && typeof ti.entity_schema === 'object'
        && ti.entity_schema.properties && typeof ti.entity_schema.properties === 'object')
        ? Object.keys(ti.entity_schema.properties)
        : [];
    const intentFormat = ti.intent?.format || 'text';
    return [
        {
            value: '',
            label: '— not mapped —',
            group: ''
        },
        {
            value: `intent.${intentFormat}`,
            label: `intent.${intentFormat}`,
            group: 'intent'
        },
        ...contextKeys.map((k) => ({
            value: `context.${k}`,
            label: `context.${k}`,
            group: 'context'
        })),
        ...propensityKeys.map((k) => ({
            value: `propensity.${k}`,
            label: `propensity.${k}`,
            group: 'propensity'
        })),
        ...entityProps.map((k) => ({
            value: `entity.${k}`,
            label: `entity.${k}`,
            group: 'entity'
        }))
    ];
}
